import React, { useMemo }               from 'react';
import { TDataTableRow }                from '@typings/components';
import { IIntranetUser }                from '@typings/manageIntranetUsers';
import {
  EUserStatus,
  EUserRole,
  EAppPage,
}                                       from '@enums/common';
import { EIntraUsersPage }              from '@enums/manageIntranetUsers';
import { ROWS_PER_PAGE }                from '@utils/suggestions';
import { INTRANET_USER_STATUSES }       from '@modules/manageIntranetUsers/utils/suggestions';
import { formatFullName }               from '@utils/commonFunctions';
import {
  Button,
  Select,
  DataTable,
  SearchPagination,
}                                       from '@components/index';
import { INTRANET_USERS_TABLE_HEADERS } from '../utils/tableHeaders';

const getTableRows = (
  users              : IIntranetUser[] = [],
  isDisabledChange   : boolean,
  handleChangeStatus : (userId: string, status: EUserStatus) => void,
): TDataTableRow[] => users.map((user) => [
  {
    value : formatFullName(user),
    link  : `${EAppPage.ManageIntranetUsers}/${user.id}/${EIntraUsersPage.edit}`,
  },
  {
    align : 'center',
    value : user.roles.includes(EUserRole.SuperAdmin)
      ? 'Permanently Active'
      : (
        <Select
          align    = "center"
          options  = {INTRANET_USER_STATUSES}
          value    = {user.status}
          disabled = {isDisabledChange}
          onChange = {
            (event: React.ChangeEvent<HTMLInputElement>): void =>
              handleChangeStatus(user.id, +event.target.value)
          }
        />
      ),
  },
  {
    value: (<a href={`mailto:${user.email}`}>{user.email}</a>),
  },
]);

interface IProps {
  intranetUsers      : IIntranetUser[];
  totalUsersCount    : number;
  isLoading          : boolean;
  currentPage        : number;
  isSuperAdmin       : boolean;
  handleAddNewUser   : () => void;
  handleChangePage   : (page: number) => void;
  handleChangeStatus : (userId: string, status: EUserStatus) => Promise<void>;
}

export const IntranetUsersListPage: React.FC<IProps> = ({
  totalUsersCount,
  isLoading,
  currentPage,
  intranetUsers = [],
  isSuperAdmin,
  handleAddNewUser,
  handleChangePage,
  handleChangeStatus,
}) => {
  const tableRows = useMemo<TDataTableRow[]>(
    () => getTableRows(intranetUsers, !isSuperAdmin, handleChangeStatus),
    [handleChangeStatus, intranetUsers, isSuperAdmin],
  );

  return (
    <>
      {isSuperAdmin && (
        <Button
          label   = "Add New Intranet User"
          onClick = {handleAddNewUser}
        />
      )}

      <DataTable
        rows     = {tableRows}
        headers  = {INTRANET_USERS_TABLE_HEADERS}
        maxwidth = "800px"
        loading  = {isLoading}
      />

      {!!totalUsersCount && (
        <SearchPagination
          totalItems       = {totalUsersCount}
          currentPage      = {currentPage}
          rowsPerPage      = {ROWS_PER_PAGE}
          handleChangePage = {handleChangePage}
        />
      )}
    </>
  );
};
